import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SKIP_GLOBAL_ERROR_HANDLING } from '../interceptor/http-error.interceptor';
import { hasCookieSession } from '../utils/auth-session';
import { LoggedInUserKeys, LoggedInUserService } from './logged-in-user.service';

/**
 * Session work that needs HttpClient. Kept out of LoggedInUserService because the HTTP
 * interceptors inject that one, and HttpClient there would be a circular dependency.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthSessionService {
  private readonly upgradeUrl = environment.baseUrl + '/api/v1/auth/upgrade';

  constructor(
    private readonly http: HttpClient,
    private readonly loggedInUserService: LoggedInUserService,
  ) {}

  /**
   * Moves a session that still keeps its token in storage onto the HttpOnly cookie, then
   * drops the token from storage. The auth interceptor sends the stored token as Bearer.
   * Anything but 204 leaves the session as it is: an older backend (404), a dead session
   * (401, the next API call logs out as usual) or the rate limit (429, retried next load).
   */
  async upgradeLegacySession(): Promise<void> {
    if (!this.loggedInUserService.getLegacyToken()) {
      return;
    }
    try {
      const response = await firstValueFrom(
        this.http.post(this.upgradeUrl, null, {
          observe: 'response',
          // A 401 here only means the old token is dead; let the next real call decide.
          context: new HttpContext().set(SKIP_GLOBAL_ERROR_HANDLING, true),
        }),
      );
      if (response.status !== 204 || !hasCookieSession()) {
        return;
      }
      // Re-saving strips the token now that the cookie session exists.
      const user = this.loggedInUserService.getLoggedInUser(LoggedInUserKeys.LOGGED_IN_USER);
      this.loggedInUserService.setLoggedInUser(LoggedInUserKeys.LOGGED_IN_USER, user);
    } catch {
      // Header mode keeps working; nothing to do.
    }
  }
}
